import React, { useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Controller } from "react-hook-form";
import Select from "react-select";

import { useTournamentContext } from "../contexts/Tournament";
import { findHomeLeague } from "../lib/findHomeLeague";
import { ChildTournamentTeams } from "../types/tournament";

import { useAppSelector } from "../../../../../hooks/reduxHooks";
import { ITournament } from "../../../../../types/api";

export const useBuildTeamHomeLeagues = ({
    teams,
}: ChildTournamentTeams): React.JSX.Element[] | undefined => {
    const { methods, tournament: childTournament } = useTournamentContext();
    const tournament = childTournament as ITournament;

    const homeLeagues = useAppSelector(
        state => state.tournamentmanagement.homeLeagues,
    );

    const [isTextInputVisible, setIsTextInputVisible] = useState<
        Record<number, boolean>
    >(() => {
        return (
            teams?.reduce(
                (acc, t) => {
                    const homeLeague = findHomeLeague(
                        t.value,
                        tournament?.schedulingData.homeLeagues,
                        homeLeagues,
                    );
                    acc[t.value] = homeLeague?.value === 0;
                    return acc;
                },
                {} as Record<number, boolean>,
            ) || {}
        );
    });

    return teams?.map((t, idx) => {
        const homeLeague = findHomeLeague(
            t.value,
            tournament.schedulingData.homeLeagues,
            homeLeagues,
        );

        return (
            <Form.Group key={idx} className="team" as={Row}>
                <Form.Label column md={6}>
                    {t.label}
                </Form.Label>
                <Col md={6}>
                    {isTextInputVisible[t.value] ? (
                        <Form.Control
                            {...methods.register(
                                `homeLeagues.${t.value}.league`,
                            )}
                            type="text"
                            value={homeLeague?.label}
                            placeholder="Enter League Name"
                            onChange={e => {
                                const value = e.target.value;
                                if (!value) {
                                    setIsTextInputVisible(prev => {
                                        return {
                                            ...prev,
                                            [t.value]: false,
                                        };
                                    });
                                    methods.setValue(
                                        `schedulingData.homeLeagues.${t.value}`,
                                        null,
                                    );
                                }
                            }}
                        />
                    ) : (
                        <Controller
                            name={`schedulingData.homeLeagues.${t.value}`}
                            control={methods.control}
                            defaultValue={homeLeague || undefined}
                            render={({ field }) => {
                                return (
                                    <Select
                                        {...field}
                                        isSearchable={true}
                                        defaultValue={homeLeague || undefined}
                                        isClearable={true}
                                        required={false}
                                        options={[
                                            ...homeLeagues.map(l => {
                                                return {
                                                    value: l.id,
                                                    label: l.name,
                                                };
                                            }),
                                            {
                                                value: 0,
                                                label: "Other (Enter League Name)",
                                            },
                                        ]}
                                        onChange={o => {
                                            if (o?.value === 0) {
                                                setIsTextInputVisible(prev => {
                                                    return {
                                                        ...prev,
                                                        [t.value]: true,
                                                    };
                                                });
                                                methods.setValue(
                                                    `schedulingData.homeLeagues.${t.value}`,
                                                    {
                                                        value: undefined,
                                                        label: undefined,
                                                    },
                                                );
                                            }
                                            field.onChange(o);
                                        }}
                                    />
                                );
                            }}
                        />
                    )}
                </Col>
            </Form.Group>
        );
    });
};
